import { useEffect, useRef, useState } from "react";
import { api, areSesiune, scrieTokenuri, candExpiraSesiunea, mesajEroare } from "./api.js";
import Login from "./Login.jsx";
import Utilizatori, { ETICHETE } from "./Utilizatori.jsx";
import Nomenclator from "./Nomenclator.jsx";
import Catalog from "./Catalog.jsx";
import Operatii from "./Operatii.jsx";
import Clienti from "./Clienti.jsx";
import Parc from "./Parc.jsx";
import Echipe from "./Echipe.jsx";
import Documente from "./Documente.jsx";
import Interventii from "./Interventii.jsx";
import Stoc from "./Stoc.jsx";
import Tichete from "./Tichete.jsx";
import Oferte from "./Oferte.jsx";
import Notificari from "./Notificari.jsx";
import Mentenanta from "./Mentenanta.jsx";
import Restante from "./Restante.jsx";
import Garantii from "./Garantii.jsx";
import Abateri from "./Abateri.jsx";
import Azi from "./Azi.jsx";
import Fluxuri from "./Fluxuri.jsx";
import BandaFlux from "./BandaFlux.jsx";
import IndicatorTeren from "./IndicatorTeren.jsx";
import Portal from "./Portal.jsx";
import Rapoarte from "./Rapoarte.jsx";
import OfertaPublica from "./OfertaPublica.jsx";
import PlasaDeSiguranta from "./PlasaDeSiguranta.jsx";
import Drepturi from "./Drepturi.jsx";
import Jurnal from "./Jurnal.jsx";
import Jurisdictie from "./Jurisdictie.jsx";
import Punti from "./Punti.jsx";
import Supraveghere from "./Supraveghere.jsx";
import Conformitate from "./Conformitate.jsx";
import Facturare from "./Facturare.jsx";
import { tr, useLimba } from "./i18n.js";

/**
 * Modulele din SoW, în ordinea fazelor de livrare. Sunt listate toate de la
 * început, dar numai cele construite sunt accesibile — restul arată explicit
 * în ce fază vin, ca să se vadă unde e produsul, nu doar ce merge azi.
 */
/**
 * `roluri` spune cine poate deschide secțiunea. Fără el, un tehnician ateriza
 * pe „Utilizatori" — prima din listă și implicită — și primea 403 în față la
 * fiecare autentificare. Tabul dispare acum pentru cine n-are ce căuta acolo,
 * iar secțiunea de pornire e prima pe care contul o poate chiar folosi.
 */
const ADMIN = ["ADMIN_TENANT", "MANAGER_TEHNIC"];
/**
 * D14: tehnicianul vede lucrarea, nu banii. O ofertă e o listă de bani de la un
 * capăt la altul, deci tabul nici nu apare — nu e ascuns un buton care oricum
 * ar da 403, e o secțiune care nu-l privește.
 */
const BANI = [
  "ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR", "FINANCIAR", "AUDITOR",
  "CLIENT_ADMIN", "CLIENT_UTILIZATOR", "CLIENT_FINANCIAR",
];
/**
 * Bucătăria firmei de service: nomenclatorul, biblioteca de operații, stocul
 * și oamenii. Clientul deservit n-are ce căuta acolo — nici măcar ca tab.
 * Serverul îl refuza deja, dar refuzul ajungea pe ecran ca „Rolul tău nu are
 * acces la această operație", adică patru taburi care arată ca o aplicație
 * stricată. Un tab pe care nu-l poți deschide nu e o măsură de securitate,
 * e o promisiune încălcată.
 */
const INTERN = [
  "ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR",
  "TEHNICIAN", "MAGAZIONER", "FINANCIAR", "AUDITOR",
];
/**
 * Ce vede un cont al clientului deservit: exact trei lucruri, cele pe care le-a
 * cerut beneficiarul — deschide și urmărește tichete, își vede procesele-verbale
 * ale intervențiilor, acceptă oferte.
 *
 * Restul — parcul, dosarul tehnic, planul de mentenanță, backlogul, catalogul —
 * nu erau o scăpare de izolare (vedea doar ce e al lui), dar erau mult peste ce
 * are nevoie și cu atât mai mult de explicat. Serverul le-a fost închis odată cu
 * taburile, ca aplicația să nu promită din meniu ce refuză în față.
 */
const CU_CLIENT = [...INTERN, "CLIENT_ADMIN", "CLIENT_UTILIZATOR", "CLIENT_FINANCIAR"];

/**
 * Bara de navigare, grupată.
 *
 * Cu 20 de secțiuni, o listă plată se rupea pe două rânduri chiar pe desktop și
 * ocupa jumătate de ecran pe telefon. Grupurile de mai jos urmăresc felul în
 * care se lucrează, nu ordinea în care s-au construit modulele: ce faci azi
 * (Operațional), pe cine deservești (Clienți), ce vinzi (Comercial), ce ai în
 * casă (Resurse), ce măsori (Analize), cine are voie (Administrare).
 *
 * Portalul rămâne separat, primul: pentru contul de client e chiar pagina de
 * pornire, iar dispecerul îl deschide când clientul îl sună.
 */
const GRUPURI = [
  { cod: "portal", titlu: "Portal client" },
  { cod: "operational", titlu: "Operațional" },
  { cod: "clienti", titlu: "Clienți" },
  { cod: "comercial", titlu: "Comercial" },
  { cod: "resurse", titlu: "Resurse" },
  { cod: "analize", titlu: "Analize" },
  { cod: "administrare", titlu: "Administrare" },
];

/**
 * Sub pragul ăsta, gruparea ar strica în loc să ajute: un cont de client vede
 * cinci secțiuni, iar a-i ascunde Tichetele într-un meniu „Operațional" de
 * două intrări e mai rău decât o bară plată de cinci butoane. Deci fiecare rol
 * primește forma potrivită LUI, nu forma potrivită celui mai încărcat rol.
 */
const PRAG_GRUPARE = 9;

const SECTIUNI = [
  // Portalul stă primul: pentru un cont de client e chiar pagina de pornire,
  // iar pentru firmă e ecranul pe care-l deschide când clientul o sună.
  /*
   * „Azi" stă primul pentru firmă: e ecranul cu care începe ziua. Până acum
   * fiecare rol ateriza pe prima secțiune vizibilă — adică pe Portalul
   * clientului, inclusiv tehnicianul, care n-are ce căuta acolo. Secțiunea de
   * pornire e tot `vizibile[0]`, doar că acum prima e cea potrivită.
   *
   * Contul de client NU o vede: pentru el pagina de început e chiar portalul
   * lui, care spune același lucru, dar despre el.
   */
  { cod: "azi", titlu: "Azi", gata: true, roluri: INTERN, grup: "portal" },
  /*
   * „Cum se lucrează" — diagramele celor șase fluxuri de proces. E deschisă
   * TUTUROR, inclusiv conturilor de client: procesul nu e un secret al firmei,
   * iar un client care înțelege de ce i se cere să accepte o propunere de
   * vizită sună mai puțin. Pașii pe care rolul nu-i lucrează rămân desenați,
   * dar nu sunt apăsabili — un proces cu găuri ar fi mai greu de citit decât
   * unul complet.
   */
  { cod: "fluxuri", titlu: "Cum se lucrează", gata: true, roluri: CU_CLIENT, grup: "portal" },
  { cod: "portal", titlu: "Portal client", gata: true, roluri: CU_CLIENT , grup: "portal" },
  { cod: "utilizatori", titlu: "Utilizatori", gata: true, roluri: ADMIN , grup: "administrare" },
  { cod: "drepturi", titlu: "Drepturi", gata: true, roluri: ADMIN , grup: "administrare" },
  // §39 — jurisdicția. Citirea e deschisă firmei: un dispecer trebuie să poată
  // vedea pe ce fus i se măsoară termenul. Niciun rol de client — regimul
  // fiscal al prestatorului e treaba lui.
  { cod: "jurisdictie", titlu: "Jurisdicție", gata: true, roluri: INTERN, grup: "administrare" },
  /*
   * Punțile către alte aplicații, și maparea obiectivelor lor.
   *
   * Deschis întregii firme, nu doar administratorului: contractul de sesizări
   * (§6) cere ca o referință nemapată să SE VADĂ, iar cine observă asta e
   * dispecerul care se uită oricum peste tichete dimineața. Butoanele de
   * mapare rămân după matrice — ecranul le arată doar cui are dreptul.
   * Niciun rol de client: cine vorbește cu cine, între aplicațiile firmei, e
   * treaba firmei.
   */
  { cod: "punti", titlu: "Punți și mapări", gata: true, roluri: INTERN, grup: "administrare" },
  /*
   * Supravegherea parcului — unde se strică ceva anormal de des.
   *
   * Deschis întregii firme: cine observă dimineața că un obiectiv se strică
   * mereu e dispecerul care se uită oricum peste tichete, nu administratorul.
   * Bifele de pe matrice rămân după drepturi — ecranul le arată doar cui poate
   * schimba. Niciun rol de client: e o judecată a firmei despre parcul lui.
   */
  { cod: "supraveghere", titlu: "Supravegherea parcului", gata: true, roluri: INTERN, grup: "administrare" },
  { cod: "nomenclator", titlu: "Nomenclator", gata: true, roluri: INTERN , grup: "resurse" },
  { cod: "catalog", titlu: "Catalog", gata: true, roluri: INTERN , grup: "comercial" },
  { cod: "operatii", titlu: "Operații", gata: true, roluri: INTERN , grup: "resurse" },
  { cod: "clienti", titlu: "Clienți", gata: true, roluri: INTERN , grup: "clienti" },
  { cod: "parc", titlu: "Parc instalat", gata: true, roluri: INTERN , grup: "clienti" },
  { cod: "echipe", titlu: "Oameni și echipe", gata: true, roluri: INTERN , grup: "resurse" },
  { cod: "documente", titlu: "Dosar tehnic", gata: true, roluri: INTERN , grup: "clienti" },
  { cod: "interventii", titlu: "Intervenții", gata: true, roluri: CU_CLIENT , grup: "operational" },
  { cod: "stoc", titlu: "Stoc", gata: true, roluri: INTERN , grup: "resurse" },
  { cod: "tichete", titlu: "Tichete", gata: true, roluri: CU_CLIENT , grup: "operational" },
  { cod: "oferte", titlu: "Oferte", gata: true, roluri: BANI , grup: "comercial" },
  { cod: "preventiv", titlu: "Mentenanță", gata: true, roluri: INTERN , grup: "operational" },
  { cod: "restante", titlu: "Backlog", gata: true, roluri: INTERN , grup: "operational" },
  // Garanția o vede și clientul: fără ea n-are cum să știe dacă o reparație
  // i se facturează. Returul la furnizor e ascuns în ecran, nu aici.
  { cod: "garantii", titlu: "Garanții", gata: true, roluri: CU_CLIENT , grup: "clienti" },
  // Măsoară oamenii firmei, nu lucrarea clientului — deci niciun rol de client.
  { cod: "abateri", titlu: "Estimat vs. realizat", gata: true, roluri: INTERN , grup: "analize" },
  // Măsoară cum lucrează firma. Ce are voie clientul să vadă din ele e deja
  // în portal, îngustat la el — deci niciun rol de client aici.
  { cod: "rapoarte", titlu: "Rapoarte", gata: true, roluri: INTERN , grup: "analize" },
  // Obligațiile firmei prestatoare — licența ei, atestatele oamenilor ei — sunt
  // strict interne (SoW §20, regula 10). Ce are voie clientul să vadă din ele
  // e dosarul lui, iar dosarul se predă, nu se răsfoiește: deci niciun rol de
  // client aici.
  { cod: "conformitate", titlu: "Conformitate", gata: true, roluri: INTERN , grup: "analize" },
  // MT12.6 / §39 — predarea către facturare. Lista de facturat e o listă de
  // bani de la un capăt la altul, deci nu apare tehnicianul, nu apare
  // magazionerul și nu apare niciun rol de client: clientul își vede
  // procesele-verbale în portal, nu ce urmează să i se factureze.
  { cod: "facturare", titlu: "Facturare", gata: true, grup: "comercial",
    roluri: ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR", "AUDITOR"] },
  // Jurnalul de audit e îngust prin design: administratorul firmei, ca să poată
  // răspunde la „cine a schimbat asta", și auditorul, al cărui rol există exact
  // pentru el. Un jurnal răsfoit de toată lumea nu mai e un jurnal de audit.
  { cod: "jurnal", titlu: "Jurnal de audit", gata: true, grup: "administrare",
    roluri: ["ADMIN_TENANT", "AUDITOR"] },
];

/**
 * Linkul de acceptare a unei oferte se deschide FĂRĂ cont. Verificarea rutei
 * stă înaintea oricărei verificări de sesiune: altfel clientul ar ateriza pe
 * ecranul de autentificare al unei aplicații în care n-are ce căuta.
 */
function tokenOferta() {
  const m = window.location.pathname.match(/^\/oferta\/([0-9a-f]{64})$/i);
  return m ? m[1] : null;
}

/**
 * Bara de navigare: plată pentru rolurile cu puține secțiuni, grupată pentru
 * cele cu multe (vezi `PRAG_GRUPARE`).
 *
 * Meniurile se deschid la CLIC, nu la hover: pe telefon nu există hover, iar un
 * meniu care se deschide singur când treci mouse-ul pe lângă el se deschide și
 * când nu vrei.
 */
function Navigatie({ sectiuni, curenta, alege }) {
  const [deschis, setDeschis] = useState(null);
  const bara = useRef(null);

  // Un clic în afara barei închide meniul. Fără asta rămâne deschis peste
  // conținut și acoperă exact ce voiai să citești.
  useEffect(() => {
    if (!deschis) return undefined;
    const inAfara = (e) => { if (bara.current && !bara.current.contains(e.target)) setDeschis(null); };
    const peEscape = (e) => { if (e.key === "Escape") setDeschis(null); };
    document.addEventListener("mousedown", inAfara);
    document.addEventListener("keydown", peEscape);
    return () => {
      document.removeEventListener("mousedown", inAfara);
      document.removeEventListener("keydown", peEscape);
    };
  }, [deschis]);

  const buton = (s, clasa) => (
    <button
      key={s.cod}
      className={clasa + (s.cod === curenta ? " activ" : "")}
      onClick={() => { alege(s.cod); setDeschis(null); }}
      title={s.gata ? undefined : tr("Se livrează în faza {0}", s.faza)}
    >
      {tr(s.titlu)}
      {!s.gata && <span className="eticheta" style={{ marginLeft: ".45rem" }}>{s.faza}</span>}
    </button>
  );

  if (sectiuni.length <= PRAG_GRUPARE) {
    return <nav className="navigatie" ref={bara}>{sectiuni.map((s) => buton(s, "tab"))}</nav>;
  }

  return (
    <nav className="navigatie" ref={bara}>
      {GRUPURI.map((g) => {
        const ale = sectiuni.filter((s) => s.grup === g.cod);
        if (ale.length === 0) return null;
        // Un grup rămas cu o singură secțiune nu e un meniu, e un buton: un
        // meniu cu o intrare cere două clicuri pentru ce încăpea într-unul.
        if (ale.length === 1) return buton(ale[0], "tab");

        const aici = ale.some((s) => s.cod === curenta);
        return (
          <div className="tab-grup" key={g.cod}>
            <button
              className={"tab" + (aici ? " activ" : "") + (deschis === g.cod ? " tab-deschis" : "")}
              aria-expanded={deschis === g.cod}
              onClick={() => setDeschis(deschis === g.cod ? null : g.cod)}
            >
              {tr(g.titlu)}
              <span className="tab-sageata" aria-hidden="true">▾</span>
            </button>
            {deschis === g.cod && (
              <div className="tab-meniu">{ale.map((s) => buton(s, "tab-meniu-item"))}</div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

/**
 * Comutatorul de limbă. Stă în bara de sus, lângă ieșire, fiindcă e o
 * preferință a persoanei, nu o setare a firmei: două conturi ale aceleiași
 * firme pot lucra în limbi diferite, pe același ecran.
 */
function ComutatorLimba({ limbaCurenta, schimba }) {
  return (
    <div className="comutator-limba" role="group" aria-label={tr("Limbă / Language")}>
      {["ro", "en"].map((l) => (
        <button
          key={l}
          type="button"
          className={"comutator-limba-optiune" + (l === limbaCurenta ? " activ" : "")}
          aria-pressed={l === limbaCurenta}
          onClick={() => schimba(l)}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function App() {
  const tokenPublic = tokenOferta();
  // Limba e ținută și ca stare, nu doar în modul: schimbarea ei trebuie să
  // RErandeze arborele, altfel `tr()` ar da textul nou abia la următoarea
  // atingere a ecranului.
  const [limbaCurenta, schimbaLimba] = useLimba();
  const [eu, setEu] = useState(null);
  const [verifica, setVerifica] = useState(true);
  const [sectiune, setSectiune] = useState(null);

  // Reconstruiește sesiunea la reîncărcarea paginii. Fără asta, un refresh
  // te arunca la login chiar cu un token valid în storage — bug real, trăit
  // pe Hub Client, care a apărut tocmai pentru că pasul ăsta lipsea.
  useEffect(() => {
    if (tokenPublic) { setVerifica(false); return; }
    candExpiraSesiunea(() => setEu(null));
    if (!areSesiune()) { setVerifica(false); return; }
    api.eu()
      .then((r) => setEu(r.user))
      .catch(() => { scrieTokenuri(null); setEu(null); })
      .finally(() => setVerifica(false));
  }, [tokenPublic]);

  /*
   * Escape închide fereastra de deasupra.
   *
   * Aplicația are peste 50 de ferestre, în 21 de ecrane, și fiecare își are
   * deja închiderea legată de clicul pe fundal (`.modal-fundal` cu
   * `onClick={laInchidere}`). Un ascultător global care declanșează CHIAR acel
   * clic le acoperă pe toate fără să atingă niciuna — și, mai important, fără
   * să inventeze o a doua cale de închidere, care s-ar putea comporta altfel
   * decât prima. O fereastră care n-are închidere pe fundal (fiindcă nu vrea să
   * se închidă din greșeală) rămâne, corect, neatinsă și de Escape.
   *
   * Ultima din listă e cea de deasupra: fereastra deschisă mai târziu se
   * randează mai jos în DOM.
   */
  useEffect(() => {
    const peEscape = (e) => {
      if (e.key !== "Escape") return;
      const fundaluri = document.querySelectorAll(".modal-fundal");
      if (fundaluri.length) fundaluri[fundaluri.length - 1].click();
    };
    document.addEventListener("keydown", peEscape);
    return () => document.removeEventListener("keydown", peEscape);
  }, []);

  async function iesi() {
    try { await api.logout(); } catch { /* tokenul poate fi deja expirat */ }
    scrieTokenuri(null);
    setEu(null);
  }

  if (tokenPublic) {
    return (
      <>
        <div className="bara-publica">
          <ComutatorLimba limbaCurenta={limbaCurenta} schimba={schimbaLimba} />
        </div>
        <OfertaPublica token={tokenPublic} />
      </>
    );
  }

  if (verifica) {
    return <div className="gol" style={{ paddingTop: "25vh" }}>{tr("Se verifică sesiunea…")}</div>;
  }
  if (!eu) return <Login laAutentificare={setEu} />;

  const vizibile = SECTIUNI.filter((s) => !s.roluri || s.roluri.includes(eu.rol));
  const activa = vizibile.find((s) => s.cod === sectiune) || vizibile[0];

  return (
    <>
      <header className="bara">
        <div className="bara-marca">
          <div className="bara-sigla">MT</div>
          <div>
            <div>{tr("Mentenanță Tehnică")}</div>
            <div className="bara-cod">CentralSec</div>
          </div>
        </div>
        <div className="bara-spatiu" />
        {/* MT19 — cât mai are telefonul de urcat. Lângă clopoțel, fiindcă e
            aceeași întrebare: „a ajuns ce am făcut?" */}
        <IndicatorTeren />
        <ComutatorLimba limbaCurenta={limbaCurenta} schimba={schimbaLimba} />
        <Notificari laDeschidereEntitate={(entitate) => {
          if (entitate === "Tichet") setSectiune("tichete");
          if (entitate === "Oferta") setSectiune("oferte");
          if (entitate === "FisaInterventie" || entitate === "Vizita") setSectiune("interventii");
          if (entitate === "PlanMentenanta") setSectiune("preventiv");
          if (entitate === "LucrareRestanta") setSectiune("restante");
        }} />
        <div className="bara-cine">
          <strong>{eu.numeComplet}</strong>
          <span>{tr(ETICHETE[eu.rol] || eu.rol)}</span>
        </div>
        <button className="buton buton-mic buton-sters" style={{ color: "#cfe0da", borderColor: "#2a3a35" }} onClick={iesi}>
          {tr("ieși")}
        </button>
      </header>

      <Navigatie sectiuni={vizibile} curenta={activa.cod} alege={setSectiune} />

      <BandaFlux sectiune={activa.cod} mergiLa={setSectiune} />

      <PlasaDeSiguranta cheie={activa.cod}>
      {activa.cod === "fluxuri" ? (
        <Fluxuri mergiLa={setSectiune} sectiuniPermise={vizibile.map((v) => v.cod)} />
      ) : activa.cod === "azi" ? (
        <Azi eu={eu} mergiLa={setSectiune} />
      ) : activa.cod === "utilizatori" ? (
        <Utilizatori eu={eu} />
      ) : activa.cod === "jurisdictie" ? (
        <Jurisdictie eu={eu} />
      ) : activa.cod === "punti" ? (
        <Punti eu={eu} />
      ) : activa.cod === "supraveghere" ? (
        <Supraveghere eu={eu} />
      ) : activa.cod === "drepturi" ? (
        <Drepturi eu={eu} />
      ) : activa.cod === "nomenclator" ? (
        <Nomenclator eu={eu} />
      ) : activa.cod === "catalog" ? (
        <Catalog eu={eu} />
      ) : activa.cod === "operatii" ? (
        <Operatii eu={eu} />
      ) : activa.cod === "clienti" ? (
        <Clienti eu={eu} />
      ) : activa.cod === "parc" ? (
        <Parc eu={eu} />
      ) : activa.cod === "echipe" ? (
        <Echipe eu={eu} />
      ) : activa.cod === "documente" ? (
        <Documente eu={eu} />
      ) : activa.cod === "interventii" ? (
        <Interventii eu={eu} />
      ) : activa.cod === "stoc" ? (
        <Stoc eu={eu} />
      ) : activa.cod === "tichete" ? (
        <Tichete eu={eu} />
      ) : activa.cod === "oferte" ? (
        <Oferte eu={eu} />
      ) : activa.cod === "preventiv" ? (
        <Mentenanta eu={eu} />
      ) : activa.cod === "restante" ? (
        <Restante eu={eu} />
      ) : activa.cod === "garantii" ? (
        <Garantii eu={eu} />
      ) : activa.cod === "abateri" ? (
        <Abateri eu={eu} />
      ) : activa.cod === "portal" ? (
        <Portal eu={eu} />
      ) : activa.cod === "rapoarte" ? (
        <Rapoarte />
      ) : activa.cod === "conformitate" ? (
        <Conformitate eu={eu} />
      ) : activa.cod === "facturare" ? (
        <Facturare eu={eu} />
      ) : activa.cod === "jurnal" ? (
        <Jurnal />
      ) : (
        <div className="continut">
          <h2>{tr(activa.titlu)}</h2>
          <p className="subtitlu">{tr("Se livrează în faza")} {activa.faza}.</p>
          <div className="panou">
            <div className="panou-corp gol">
              {tr("Modulul e specificat în caietul de sarcini, dar încă neconstruit. Deocamdată sunt livrate F0 — identitate, roluri, izolare, audit —, nomenclatoarele din F1, clienții cu parcul instalat din F2 și nucleul operațional din F3 — stoc, fișa de intervenție, deviz, rețete — și F4 — SLA, tichete, notificări, ofertare — și F5: mentenanță preventivă cu backlog tehnic.")}
            </div>
          </div>
        </div>
      )}
      </PlasaDeSiguranta>

      <footer className="subsol">
        {tr("Modul Mentenanță Tehnică · faza F7 · platforma CentralSec")}
      </footer>
    </>
  );
}
